/**
 * Utility for accessing environment variables
 *
 * This utility provides a consistent way to access environment variables,
 * whether they're from import.meta.env (during development) or from
 * window.RUNTIME_CONFIG (in production).
 */

/**
 * Get an environment variable value
 * @param {string} key - The environment variable key (e.g., 'VITE_SERVER_URL')
 * @param {any} defaultValue - Default value if the environment variable is not found
 * @returns {any} The environment variable value or the default value
 */
export function getEnv(key, defaultValue = undefined) {
  // In production, use the runtime configuration if available
  if (window.RUNTIME_CONFIG && window.RUNTIME_CONFIG[key] !== undefined) {
    const value = window.RUNTIME_CONFIG[key];

    // Convert string boolean values to actual booleans
    if (value === 'true') return true;
    if (value === 'false') return false;

    // Convert numeric strings to numbers
    if (!isNaN(value) && value !== '') return Number(value);

    return value;
  }

  // In development, use import.meta.env
  if (import.meta.env[key] !== undefined) {
    return import.meta.env[key];
  }

  // Return default value if the key is not found
  return defaultValue;
}

/**
 * Get all environment variables
 * @returns {Object} All environment variables
 */
export function getAllEnv() {
  // In production, use the runtime configuration if available
  if (window.RUNTIME_CONFIG) {
    return { ...window.RUNTIME_CONFIG };
  }

  // In development, use import.meta.env
  return { ...import.meta.env };
}
